import type { AutomaticEvaluation } from '../../supabase/functions/_shared/proactiveAutomation';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { mirrorAuthIdentity } from '@/lib/mirrorAuthSession';
import { createConfirmedTask, taskClientKey, withTaskOwner } from '@/lib/taskMutations';
import type {
  ProactivePreferences,
  ProactiveSnapshot,
  Suggestion,
} from '../../supabase/functions/_shared/proactiveDomain';
export type {
  ProactivePreferences,
  ProactiveSnapshot,
  ProactiveSource,
  Suggestion,
} from '../../supabase/functions/_shared/proactiveDomain';
export { PROACTIVE_SOURCES } from '../../supabase/functions/_shared/proactiveDomain';
export interface ProactiveFeedbackHistory {
  suggestion_id: string;
  action: 'ignored' | 'snoozed' | 'opened';
  hidden_until: string;
  expires_at: string;
  updated_at: string;
}
export interface ProactiveGuard {
  authSessionId: string;
  isCurrent: () => boolean;
  signal?: AbortSignal;
}
const assertGuard = (guard?: ProactiveGuard) => {
  if (guard && (!guard.isCurrent() || guard.signal?.aborted)) throw new Error('invalid_session');
};
export interface ProactiveFeed {
  ok: boolean;
  user_id: string;
  preferences: ProactivePreferences;
  paid: boolean;
  snapshot: ProactiveSnapshot | null;
  next_refresh_at: string | null;
  feedback_history?: ProactiveFeedbackHistory[];
  automatic_policy_supported?: true;
  background_supported?: true;
  background_available?: boolean;
  automatic_evaluation?: AutomaticEvaluation;
}
export async function assertProactiveOwner(owner: string, guard?: ProactiveGuard) {
  assertGuard(guard);
  const { data: { session }, error } = await supabase.auth.getSession();
  assertGuard(guard);
  if (error || session?.user.id !== owner || !session.access_token ||
    (guard && (mirrorAuthIdentity(session)?.authSessionId !== guard.authSessionId ||
      mirrorAuthIdentity(session)?.ownerId !== owner))) throw new Error('invalid_session');
  return session.access_token;
}
export async function proactiveRequest(owner: string, body: Record<string, unknown>, guard?: ProactiveGuard): Promise<ProactiveFeed> {
  const token = await assertProactiveOwner(owner, guard);
  const { data, error } = await supabase.functions.invoke('proactive-assistant', {
    body,
    headers: { Authorization: 'Bearer ' + token },
    ...(guard?.signal ? { signal: guard.signal } : {}),
  });
  await assertProactiveOwner(owner, guard);
  if (error) {
    let code = 'request_failed';
    try {
      code = (await error.context?.json())?.error || code;
    } catch { /* offline */ }
    throw new Error(code);
  }
  if (data?.ok !== true || data.user_id !== owner) throw new Error('request_failed');
  return data as ProactiveFeed;
}
export interface ProactiveSourceDetail {
  title: string;
  content: string;
  from?: string;
  reference: string;
  truncated: boolean;
}
export async function readProactiveSource(owner: string, item: Suggestion, guard?: ProactiveGuard): Promise<ProactiveSourceDetail> {
  const token = await assertProactiveOwner(owner, guard);
  if (Date.parse(item.expires_at) <= Date.now()) throw new Error('suggestion_expired');
  let row: Record<string, unknown>;
  if (item.source === 'email' || item.source === 'files') {
    const email = item.source === 'email';
    const { data, error } = await supabase.functions.invoke(email ? 'email-executor' : 'files-executor', {
      headers: { Authorization: 'Bearer ' + token },
      ...(guard?.signal ? { signal: guard.signal } : {}),
      body: {
        provider: item.provider,
        action: 'get',
        ...(email ? { message_id: item.reference } : { item_id: item.reference }),
      },
    });
    if (error) throw error;
    row = email ? data?.message : data?.item;
    if (!row || data?.ok !== true || (!email && row.content_available === false)) {
      throw new Error('source_unavailable');
    }
  } else {
    const query = item.source === 'agenda'
      ? supabase.from('agenda_events').select('id,title,notes').eq('user_id', owner).eq('id', item.reference)
        .is('deleted_at', null).maybeSingle()
      : supabase.from('tasks').select('id,title').eq('user_id', owner).eq('id', item.reference).eq(
        'done',
        false,
      ).maybeSingle();
    const { data, error } = await query;
    if (error || !data) throw new Error('source_unavailable');
    row = data;
  }
  await assertProactiveOwner(owner, guard);
  const content = String(row.body_text ?? row.content_text ?? row.notes ?? '');
  return {
    truncated: content.length > 20000,
    title: String(row.subject ?? row.name ?? row.title ?? item.title).slice(0, 240),
    content: content.slice(0, 20000),
    from: typeof row.from === 'string' ? row.from : undefined,
    reference: item.reference,
  };
}
export async function createProactiveTask(owner: string, item: Suggestion, title: string, dueDate: string, guard?: ProactiveGuard) {
  return withTaskOwner(owner, guard ? () => assertGuard(guard) : undefined, async (_uid, assert) => {
    await assertProactiveOwner(owner, guard);
    assert();
    assertGuard(guard);
    const key = await taskClientKey(owner, 'proactive_task_' + owner + '_' + item.id, 'proactive_task');
    assert();
    assertGuard(guard);
    await assertProactiveOwner(owner, guard);
    const task = await createConfirmedTask({
      client_action_id: key,
      title: title.trim(),
      priority: 'Normal',
      source: 'manual',
      due_date: dueDate ? new Date(dueDate + 'T09:00:00').toISOString() : null,
      time_label: null,
    }, owner, assert);
    await assertProactiveOwner(owner, guard);
    assert();
    assertGuard(guard);
    return task;
  });
}
export async function createProactiveReply(owner: string, item: Suggestion, text: string, guard?: ProactiveGuard) {
  const token = await assertProactiveOwner(owner, guard);
  const key = 'proactive_reply_' + item.id;
  // Reopen the same plan after a lost response, including any uncertain execution.
  const existing = await (supabase as SupabaseClient).from('personal_action_plans').select('id').eq(
    'user_id',
    owner,
  ).eq('client_action_id', key).maybeSingle();
  await assertProactiveOwner(owner, guard);
  if (existing.error) throw existing.error;
  if (existing.data) {
    await assertProactiveOwner(owner, guard);
    return existing.data.id;
  }
  const { data: result, error } = await supabase.functions.invoke('personal-action-engine', {
    headers: { Authorization: 'Bearer ' + token },
    ...(guard?.signal ? { signal: guard.signal } : {}),
    body: {
      op: 'create',
      title: item.title || item.provider,
      client_action_id: key,
      steps: [{
        capability: item.provider + '.reply',
        input: { message_id: item.reference, body_text: text },
      }],
    },
  });
  if (error) throw error;
  await assertProactiveOwner(owner, guard);
  if (!result?.plan || result.plan.user_id !== owner) throw new Error('request_failed');
  return result.plan.id;
}
